import Vue from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import router from "@/router";
import SecureLS from "secure-ls";
import { getSubCompanyId } from "@/core/services/store.js";

const ls = new SecureLS({ encodingType: "aes", isCompression: true });
const baseURL = process.env.VUE_APP_BACKEND_APP_URL;
const aiBaseURL =
    process.env.VUE_APP_AI_BASE_URL ||
    "https://ai-doc.zorunluegitimler.com/api/v1";
const {
    applyBackendRequestPolicy,
    applyIsolatedExternalAuthPolicy,
    applyPublicRequestPolicy,
    isBackendEndpoint,
} = require("./api-url-policy");

export const backendHttpClient = axios.create({
    baseURL,
});

export const publicHttpClient = axios.create({
    withCredentials: false,
    xsrfCookieName: null,
    xsrfHeaderName: null,
});

// AI credentials belong to a different trust boundary. This client never receives
// the LMS bearer token and can only call the configured AI origin.
export const externalAiHttpClient = axios.create({
    baseURL: aiBaseURL,
    withCredentials: false,
    xsrfCookieName: null,
    xsrfHeaderName: null,
});

const refreshHttpClient = axios.create({
    baseURL,
});

// Preserve the legacy expectation that axios.create() inherits the API base URL.
// Authentication is intentionally never stored on the global defaults object.
axios.defaults.baseURL = baseURL;

let isRefreshing = false;
let failedQueue = [];

function getSecureValue(key, fallback = null) {
    try {
        const value = ls.get(key);
        return value === undefined || value === null || value === ""
            ? fallback
            : value;
    } catch (error) {
        return fallback;
    }
}

function getBackendPolicyOptions(includeToken = true) {
    return {
        backendBaseUrl: baseURL,
        token: includeToken ? getSecureValue("token") : null,
        language: getSecureValue("language", "tr"),
        companyId: getSubCompanyId?.value,
    };
}

const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });

    failedQueue = [];
};

function isRefreshExcludedRequest(request) {
    if (!request || !request.url) {
        return false;
    }

    return ["/api/token", "/api/logout", "/api/auth-validation"].some(
        (endpoint) => {
            try {
                return isBackendEndpoint(request.url, baseURL, endpoint);
            } catch (error) {
                return false;
            }
        }
    );
}

function redirectToLoginAfterRefreshFailure(error) {
    if (
        !error.response ||
        ![401, 429, 422].includes(error.response.status)
    ) {
        return;
    }

    const redirectUrl = window.localStorage.getItem("redirect_url");
    ls.removeAll();

    if (redirectUrl && typeof redirectUrl !== "undefined") {
        router.push({
            name: "login",
            query: { redirect: redirectUrl },
        });
    } else {
        router.push({ name: "login" });
    }
}

function handleBackendResponseError(error, requestClient) {
    const originalRequest = error.config;

    if (!error.response) {
        if (error.code !== "ERR_UNSAFE_REQUEST_URL") {
            console.error("Network Error:", error.message);
        }
        return Promise.reject(error);
    }

    if (error.response.status === 403) {
        router.push({ name: "403" });
        return Promise.reject(error);
    }

    if (
        error.response.status === 401 &&
        isRefreshExcludedRequest(originalRequest)
    ) {
        return Promise.reject(error);
    }

    if (
        error.response.status !== 401 ||
        !originalRequest ||
        originalRequest._retry
    ) {
        return Promise.reject(error);
    }

    if (isRefreshing) {
        originalRequest._retry = true;
        return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
        }).then(() => requestClient.request(originalRequest));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    return refreshHttpClient
        .post("/api/token/refresh", {
            refresh_token: getSecureValue("refresh_token"),
        })
        .then(({ data }) => {
            ls.set("token", data.access_token);
            ls.set("refresh_token", data.refresh_token);
            return data.access_token;
        })
        .then(
            (accessToken) => {
                isRefreshing = false;
                processQueue(null, accessToken);
                return requestClient.request(originalRequest);
            },
            (refreshError) => {
                isRefreshing = false;
                processQueue(refreshError, null);
                redirectToLoginAfterRefreshFailure(refreshError);
                return Promise.reject(refreshError);
            }
        );
}

function installBackendInterceptors(client) {
    client.interceptors.request.use(
        (config) =>
            applyBackendRequestPolicy(config, getBackendPolicyOptions(true)),
        (error) => Promise.reject(error)
    );
    client.interceptors.response.use(
        (response) => response,
        (error) => handleBackendResponseError(error, client)
    );
}

installBackendInterceptors(backendHttpClient);

// A large part of the legacy application imports axios directly. Keep those
// calls working, but run them through the same exact-origin policy.
installBackendInterceptors(axios);

refreshHttpClient.interceptors.request.use(
    (config) =>
        applyBackendRequestPolicy(config, getBackendPolicyOptions(false)),
    (error) => Promise.reject(error)
);

publicHttpClient.interceptors.request.use(
    (config) => applyPublicRequestPolicy(config),
    (error) => Promise.reject(error)
);

externalAiHttpClient.interceptors.request.use(
    (config) => applyIsolatedExternalAuthPolicy(config, aiBaseURL),
    (error) => Promise.reject(error)
);

function createRequestConfig(config = {}) {
    const safeConfig =
        config && typeof config === "object" && !Array.isArray(config)
            ? config
            : {};
    return {
        ...safeConfig,
        headers: safeConfig.headers ? safeConfig.headers : {},
    };
}

function rethrowGetError(error) {
    if (error && error.code === "ERR_UNSAFE_REQUEST_URL") {
        throw error;
    }
    throw new Error(`ApiService ${error.message || error}`);
}

/**
 * Service to call HTTP request via Axios
 */
const ApiService = {
    init() {
        Vue.use(VueAxios, backendHttpClient);
    },

    /**
     * Kept for legacy callers. Headers are resolved for every request so that
     * refreshed tokens are never persisted in Axios defaults.
     */
    setHeader() {
        return backendHttpClient;
    },

    query(resource, params, config = {}) {
        const requestConfig = createRequestConfig(config);
        requestConfig.params = params;
        return backendHttpClient
            .get(resource, requestConfig)
            .catch(rethrowGetError);
    },

    /**
     * Send the GET HTTP request
     * @param resource
     * @param slug
     * @param config
     * @returns {*}
     */
    get(resource, slug = "", config = {}) {
        const requestConfig = createRequestConfig(config);
        return backendHttpClient
            .get(`${resource}${slug ? "/" : ""}${slug}`, requestConfig)
            .catch(rethrowGetError);
    },

    /**
     * Set the POST HTTP request
     * @param resource
     * @param params
     * @param config
     * @returns {*}
     */
    post(resource, params, config = {}) {
        return backendHttpClient.post(
            `${resource}`,
            params,
            createRequestConfig(config)
        );
    },

    /**
     * Send the UPDATE HTTP request
     * @param resource
     * @param slug
     * @param params
     * @param config
     * @returns {IDBRequest<IDBValidKey> | Promise<void>}
     */
    update(resource, slug, params, config = {}) {
        return backendHttpClient.put(
            `${resource}${slug ? "/" : ""}${slug}`,
            params,
            createRequestConfig(config)
        );
    },

    /**
     * Send the PUT HTTP request
     * @param resource
     * @param params
     * @param config
     * @returns {IDBRequest<IDBValidKey> | Promise<void>}
     */
    put(resource, params, config = {}) {
        return backendHttpClient.put(
            `${resource}`,
            params,
            createRequestConfig(config)
        );
    },

    /**
     * Send the DELETE HTTP request
     * @param resource
     * @param config
     * @returns {*}
     */
    delete(resource, config = {}) {
        return backendHttpClient
            .delete(resource, createRequestConfig(config))
            .catch((error) => {
                if (error && error.code === "ERR_UNSAFE_REQUEST_URL") {
                    throw error;
                }
                const apiMessage =
                    error.response?.data?.message ||
                    error.response?.data?.error ||
                    error.message;

                throw new Error(apiMessage);
            });
    },
};

export default ApiService;
